<template>
  <section v-if="!isPageLoading" class="home">
    <div
      v-if="!isFreeTrialUser"
      :class="[
        isAuthenticated && !isFreeTrialUser ? 'subscriber-bg' : 'anonymous-bg',
      ]"
    >
      <div class="background background--1"></div>
      <div class="background background--2"></div>
      <div class="background background--3"></div>
      <div class="background background--4"></div>
    </div>
    <AnonymousView
      v-if="!isAuthenticated"
      :algoliaKeys="algoliaKeys"
      :isFreeTrialUser="isFreeTrialUser"
      :isMobile="isMobile"
      :isTablet="isTablet"
      :windowWidth="windowWidth"
      :carouselMetrics="carouselMetrics"
    />
    <SubscriberView
      v-if="isAuthenticated && !isFreeTrialUser"
      :algoliaKeys="algoliaKeys"
      :isFreeTrialUser="isFreeTrialUser"
      :isMobile="isMobile"
      :isTablet="isTablet"
      :carouselMetrics="carouselMetrics"
      :recentActivity="recentActivity"
      :nextUpContent="recentActivityNextUp"
    />
    <StarterPassView
      v-if="isFreeTrialUser"
      :preferences="profilePreferences"
      :passTypes="passTypes"
      :algoliaKeys="algoliaKeys"
    />
  </section>
  <section v-else class="home">
    <FeaturedSkel />
    <RecentlyActiveSkel />
    <LoadingSpinner />
  </section>
</template>

<script>
import CONSTANTS from '@/constants'
import { mapState, mapGetters } from 'vuex'
import LoadingSpinner from '@/components/loaders/LoadingSpinner.vue'
import StarterPassView from '@/components/home/StarterPassView.vue'
import AnonymousView from '@/components/home/AnonymousView.vue'
import SubscriberView from '@/components/home/SubscriberView.vue'
import FeaturedSkel from '@/components/loaders/skeletons/home-page/FeaturedSkel.vue'
import RecentlyActiveSkel from '@/components/loaders/skeletons/home-page/RecentlyActiveSkel.vue'

export default {
  name: 'Home',
  metaInfo() {
    return {
      title: `INE - Home`,
      meta: [
        {
          vmid: 'home',
          name: 'description',
          content:
            'INE is the premier provider of online training for the IT Industry. INE’s innovative learning solution combines in-depth videos with practical, hands-on exercises.',
        },
      ],
    }
  },
  data() {
    return {
      windowWidth: window.innerWidth,
      isMobile: false,
      isTablet: false,
      carouselMetrics: CONSTANTS.HOME_PAGE.CAROUSEL_METRICS,
    }
  },
  components: {
    LoadingSpinner,
    StarterPassView,
    AnonymousView,
    SubscriberView,
    FeaturedSkel,
    RecentlyActiveSkel,
  },
  computed: {
    ...mapState({
      isAuthenticated: state => state.auth.isAuthenticated,
      algoliaKeys: state => state.search.algoliaKeys,
      learningAreas: state => state.learningAreas.learningAreas,
      passTypes: state => state.subscriptions.passTypes,
      profilePreferences: state => state.auth.profilePreferences,
      isPageLoading: state => state.app.isPageLoading,
      recentActivity: state => state.content.recentActivity,
      recentActivityNextUp: state => state.content.recentActivityNextUp,
    }),
    ...mapGetters({
      isFreeTrialUser: 'auth/isFreeTrialUser',
    }),
  },
  watch: {
    windowWidth(width) {
      this.isMobile = width <= 768
      this.isTablet = width <= 1024
    },
  },
  async created() {
    if (this.isAuthenticated && !this.isFreeTrialUser) {
      this.$store.dispatch('app/setIsPageLoading', true)
      await this.$store.dispatch('content/getRecentActivity')
    }
    this.$store.dispatch('app/setIsPageLoading', false)
  },
  mounted() {
    this.isMobile = window.innerWidth <= 768
    this.isTablet = window.innerWidth <= 1024
    this.$nextTick(() => {
      window.addEventListener('resize', () => {
        this.windowWidth = window.innerWidth
      })
    })
  },
  destroyed() {
    window.removeEventListener('resize', () => null)
  },
}
</script>

<style lang="scss" scoped>
.home {
  position: relative;
  overflow: hidden;
}
.background {
  position: absolute;
  width: 439px;
  height: 439px;
  mix-blend-mode: screen;
  filter: blur(273px);
  transform: matrix(0, -1, -1, 0, 0, 0);
  z-index: -1;
  opacity: 0.3;
  left: 150px;
  @include mobile {
    width: 508px;
    height: 509px;
  }
  &--1 {
    display: none;
  }
  &--2 {
    background: var(--orange);
    top: 3500px;
    z-index: -5;
    @include mobile {
      top: 2800px;
    }
  }
  &--3 {
    filter: blur(410.677px);
    background: var(--dark-blue);
    opacity: 0.4;
    left: 50%;
    transform: translateX(-30%);
    width: 700px;
    height: 700px;
    top: 7000px;
    @include mobile {
      top: 3950px;
      width: 508px;
      height: 509px;
    }
  }
  &--4 {
    left: 10px;
    top: 7500px;
    background: var(--orange);
    width: 711px;
    height: 711px;
    z-index: -5;
    @include mobile {
      top: 4200px;
      width: 711px;
      height: 711px;
    }
  }
}
.anonymous-bg {
  .background {
    &--1 {
      display: block;
      filter: blur(410.677px);
      background: var(--dark-blue);
      opacity: 0.4;
      left: 50%;
      transform: translateX(-50%);
      top: 800px;
    }
    &--2 {
      background: var(--orange);
      top: 1000px;
    }
    &--3 {
      filter: blur(410.677px);
      background: var(--dark-blue);
      opacity: 0.4;
      left: 50%;
      transform: translateX(-30%);
      top: 2350px;
    }
    &--4 {
      left: 10px;
      top: 2400px;
      background: var(--orange);
      width: 711px;
      height: 711px;
    }
  }
}
</style>
